import React, { useRef, useState } from 'react';
import { Btn } from '../chancey/Btn';
import { Icon } from '../chancey/Icons';
import type { ThemePalette, TypographyTokens } from '../../theme/tokens';
import type { RuleId } from '../../data/ruleSchema';
import { RULE_SCHEMA } from '../../data/ruleSchema';
import { gameById, type GameDefinition } from '../../data/games';
import type { TicketRule } from '../../types/rules';
import type { RulesStatsRuleRow } from '../../api/types';
import { useRuleBuilderStore } from '../../stores/ruleBuilderStore';
import { safeAreaPadding } from '../layout/safeArea';

/** Same footprint as pool card in DesignFiles screen-rules PoolCard. */
function PoolRecentDrawsSkeleton({ c, animated = true }: { c: ThemePalette; animated?: boolean }) {
  return (
    <div className={animated ? 'animate-pulse' : undefined} aria-hidden>
      <div className="mb-3 flex items-center gap-1.5">
        <div className="h-[18px] w-[18px] shrink-0 rounded-full" style={{ background: c.surfaceAlt }} />
        <div className="h-3 max-w-[140px] flex-1 rounded" style={{ background: c.surfaceAlt }} />
        <div className="h-[26px] w-[26px] shrink-0 rounded-full" style={{ background: c.surfaceAlt }} />
      </div>
      <div className="flex items-center justify-between py-1.5">
        <div className="h-3 w-[120px] rounded" style={{ background: c.surfaceAlt }} />
        <div className="h-4 w-[100px] rounded" style={{ background: c.surfaceAlt }} />
      </div>
      <div className="my-1.5 h-px opacity-50" style={{ background: c.line }} />
      <div className="mb-2 h-3 max-w-[160px] rounded" style={{ background: c.surfaceAlt }} />
      <div className="flex gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex-1 rounded-[10px] px-1.5 py-2.5"
            style={{ background: c.surfaceAlt, border: `0.5px solid ${c.line}` }}
          >
            <div className="mx-auto mb-1 h-2 w-10 rounded" style={{ background: c.bg }} />
            <div className="mx-auto h-6 w-8 rounded" style={{ background: c.bg }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function spreadRequiredZones(strictness: string, zones: number): number {
  const cappedZones = Math.min(zones, 5);
  if (strictness === 'strict') return Math.min(5, zones);
  if (strictness === 'medium') return Math.max(2, Math.min(cappedZones, 4));
  return Math.max(2, Math.min(3, cappedZones));
}

function spreadZoneRanges(zones: number, mainRange: number): Array<{ start: number; end: number }> {
  return Array.from({ length: zones }, (_, i) => {
    const start = Math.floor((i * mainRange) / zones) + 1;
    const end = Math.floor(((i + 1) * mainRange) / zones);
    return { start, end };
  });
}

function SpreadRuleGuide({
  c,
  typ,
  settings,
  game,
}: {
  c: ThemePalette;
  typ: TypographyTokens;
  settings: Record<string, string | number | boolean>;
  game: GameDefinition;
}) {
  const zones = Math.max(1, Math.floor(Number(settings.zones ?? 5)));
  const strictness = String(settings.strictness ?? 'medium');
  const required = spreadRequiredZones(strictness, zones);
  const ranges = spreadZoneRanges(zones, game.mainRange);
  return (
    <div
      className="rounded-[14px] p-3"
      style={{ background: c.surfaceAlt, border: `0.5px solid ${c.line}` }}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="text-[11px] font-bold uppercase" style={{ color: c.ink, fontFamily: typ.body }}>
          Number areas
        </div>
        <div className="text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
          Use any {required} of {zones}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ranges.map((range, i) => (
          <div
            key={`${range.start}-${range.end}`}
            className="min-w-[46px] flex-1 rounded-[9px] px-1.5 py-2 text-center"
            style={{
              background: c.bg,
              border: `1px solid ${c.line}`,
            }}
          >
            <div
              className="text-[9px] font-bold uppercase"
              style={{ color: c.inkMuted, fontFamily: typ.body }}
            >
              Area {i + 1}
            </div>
            <div className="mt-0.5 font-mono text-[11px] font-semibold" style={{ color: c.ink, fontFamily: typ.mono }}>
              {range.start}-{range.end}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[12px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
        Chancey splits 1-{game.mainRange} into these areas. A pick passes when the main numbers land in at least{' '}
        {required} different areas.
      </div>
    </div>
  );
}

export interface RuleDetailSheetProps {
  rule: TicketRule;
  onClose: () => void;
  onSave: (patch: Partial<TicketRule>) => void;
  onRemove: () => void;
  c: ThemePalette;
  typ: TypographyTokens;
  /** Per-rule stats from rules/stats API; shown only in this sheet, not on the list card. */
  ruleStats?: RulesStatsRuleRow | null;
  statsLoading?: boolean;
  /** Sheet-only refresh POST with current (possibly unsaved) draft settings for this rule. */
  statsRefreshing?: boolean;
  onRefreshStats?: (draftSettings: Record<string, string | number | boolean>) => void | Promise<void>;
}

export const RuleDetailSheet: React.FC<RuleDetailSheetProps> = ({
  rule,
  onClose,
  onSave,
  onRemove,
  c,
  typ,
  ruleStats,
  statsLoading = false,
  statsRefreshing = false,
  onRefreshStats,
}) => {
  const overlay = useRuleBuilderStore((s) => s.ruleSchemaOverlay);
  const activeGameId = useRuleBuilderStore((s) => s.activeGameId);
  const id = rule.id as RuleId;
  const schema = overlay[id] ?? RULE_SCHEMA[id];
  const game = gameById(activeGameId);
  const [settings, setSettings] = useState<Record<string, string | number | boolean>>({
    ...schema.defaults,
    ...(rule.settings || {}),
  });
  const settingsRef = useRef(settings);

  const update = (k: string, v: string | number | boolean) => {
    const next = { ...settingsRef.current, [k]: v };
    settingsRef.current = next;
    setSettings(next);
    onSave({ settings: next });
  };

  const presetIcon = Icon[rule.presetIcon as keyof typeof Icon] ?? Icon.sparkle;

  /** Only while first paint has no stats row yet — not during background list refetches (avoids flicker). */
  const statsBootstrapLoading = statsLoading && !ruleStats;
  const showSkeletonOnly = statsBootstrapLoading || (statsRefreshing && !ruleStats);

  const poolRecentDrawsLoaded =
    ruleStats != null ? (
      <>
        <div className="flex items-center justify-between py-1.5">
          <div className="text-xs" style={{ color: c.inkSoft, fontFamily: typ.body }}>
            Pool size after this rule
          </div>
          <div className="flex items-baseline gap-1.5">
            <div className="font-mono text-sm font-bold" style={{ color: c.accent, fontFamily: typ.mono }}>
              1 : {ruleStats.remainingPoolSizeAfterRule.toLocaleString()}
            </div>
            <div
              className="rounded px-1 py-px font-mono text-[9px] font-bold uppercase tracking-[0.08em]"
              style={{ background: c.surfaceAlt, color: c.inkMuted }}
            >
              {ruleStats.method}
            </div>
          </div>
        </div>
        <div className="my-1.5 h-px opacity-50" style={{ background: c.line }} />
        <div
          className="pb-2 pt-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: c.inkMuted, fontFamily: typ.body }}
        >
          Matches in recent draws
        </div>
        <div className="flex gap-2">
          {([50, 100, 500] as const).map((w) => (
            <div
              key={w}
              className="flex-1 rounded-[10px] px-1.5 py-2.5 text-center"
              style={{ background: c.surfaceAlt, border: `0.5px solid ${c.line}` }}
            >
              <div
                className="font-mono text-[10px] font-semibold"
                style={{ color: c.inkMuted, fontFamily: typ.mono }}
              >
                last {w}
              </div>
              <div
                className="mt-0.5 text-[22px] leading-tight"
                style={{
                  fontFamily: typ.display,
                  fontWeight: typ.displayWeight,
                  color: c.ink,
                  letterSpacing: typ.displayLetter,
                }}
              >
                {ruleStats.matches[String(w)] ?? 0}
              </div>
            </div>
          ))}
        </div>
      </>
    ) : null;

  return (
    <div
      className="absolute inset-0 z-[200] flex flex-col justify-end bg-black/45"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes spin360 { to { transform: rotate(360deg); } }
      `}</style>
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[88%] overflow-y-auto rounded-t-[28px]"
        style={{
          ...safeAreaPadding(10, 20, 32, 20),
          background: c.bg,
          animation: 'sheetUp 0.3s cubic-bezier(.2,.8,.2,1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pb-3">
          <div className="h-[5px] w-10 rounded-md" style={{ background: c.line }} />
        </div>

        <div className="mb-3.5 flex items-center gap-3">
          <div
            className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: c.gradient }}
          >
            {presetIcon(22, '#fff')}
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-[22px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              {rule.name}
            </div>
            <div className="mt-0.5 text-xs" style={{ color: c.inkMuted, fontFamily: typ.body }}>
              Rule · Edit settings
            </div>
          </div>
          <button
            type="button"
            className="flex shrink-0 items-center justify-center rounded-full border"
            style={{ width: 32, height: 32, background: c.surface, borderColor: c.line, borderWidth: 1 }}
            onClick={onClose}
            aria-label="Close"
          >
            {Icon.close(16, c.inkSoft)}
          </button>
        </div>

        <div
          className="mb-2.5 rounded-2xl p-3.5"
          style={{ background: c.surface, border: `0.5px solid ${c.line}`, marginBottom: 10 }}
        >
          <div className="mb-2 flex items-center gap-1.5">
            <div
              className="flex h-[18px] w-[18px] items-center justify-center rounded-full text-[11px] font-bold"
              style={{ background: c.accent, color: c.accentInk, fontFamily: typ.body }}
            >
              i
            </div>
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.08em]"
              style={{ fontFamily: typ.body, color: c.ink }}
            >
              How it works
            </div>
          </div>
          <div className="text-[13px] leading-relaxed" style={{ color: c.inkSoft, fontFamily: typ.body }}>
            {schema.info}
          </div>
          <div
            className="mt-2.5 flex gap-2 rounded-[10px] p-2.5"
            style={{ background: c.surfaceAlt, marginTop: 10, padding: 10, gap: 8 }}
          >
            {Icon.sparkle(14, c.accent)}
            <div className="flex-1 text-xs leading-[1.45]" style={{ fontFamily: typ.body, color: c.ink, fontSize: 12 }}>
              {schema.stat}
            </div>
          </div>
        </div>

        {(statsLoading || ruleStats || onRefreshStats) && (
          <div
            className="mb-2.5 rounded-2xl p-3.5"
            style={{ background: c.surface, border: `0.5px solid ${c.line}`, marginBottom: 10 }}
          >
            <div className="mb-3 flex items-center gap-1.5">
              <div
                className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full"
                style={{ background: c.accent2, color: c.ink }}
              >
                {Icon.stats(11, c.ink)}
              </div>
              <div
                className="text-[11px] font-semibold uppercase tracking-[0.08em]"
                style={{ color: c.ink, fontFamily: typ.body }}
              >
                Pool & recent draws
              </div>
              <div className="min-w-0 flex-1" />
              {onRefreshStats ? (
                <button
                  type="button"
                  disabled={statsRefreshing || statsBootstrapLoading}
                  className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border-0 p-0"
                  style={{
                    background: c.surfaceAlt,
                    cursor: statsRefreshing || statsBootstrapLoading ? 'default' : 'pointer',
                    opacity: statsRefreshing || statsBootstrapLoading ? 0.65 : 1,
                  }}
                  aria-label="Refresh stats"
                  onClick={(e) => {
                    e.stopPropagation();
                    void onRefreshStats(settings);
                  }}
                >
                  <span
                    className="inline-flex"
                    style={{
                      animation: statsRefreshing ? 'spin360 0.7s linear infinite' : undefined,
                    }}
                  >
                    {Icon.refresh(13, c.ink)}
                  </span>
                </button>
              ) : null}
            </div>
            <div aria-busy={statsRefreshing || statsBootstrapLoading}>
              {ruleStats ? (
                <div className="relative">
                  <div
                    style={{
                      opacity: statsRefreshing ? 0.22 : 1,
                      transition: 'opacity 0.15s ease-out',
                    }}
                    aria-hidden={statsRefreshing}
                  >
                    {poolRecentDrawsLoaded}
                  </div>
                  {statsRefreshing ? (
                    <div className="pointer-events-none absolute inset-0">
                      {/* Static ghost avoids pulse fighting the underlying bars during overlay. */}
                      <PoolRecentDrawsSkeleton c={c} animated={false} />
                    </div>
                  ) : null}
                </div>
              ) : showSkeletonOnly ? (
                <PoolRecentDrawsSkeleton c={c} />
              ) : onRefreshStats ? (
                <div
                  className="flex min-h-[136px] flex-col justify-center text-[12px]"
                  style={{ color: c.inkSoft, fontFamily: typ.body }}
                >
                  Tap Refresh to load pool size and draw counts for the settings above.
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div className="rounded-2xl p-3.5" style={{ background: c.surface, border: `0.5px solid ${c.line}` }}>
          <div
            className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: c.inkMuted, fontFamily: typ.body }}
          >
            Settings
          </div>
          <div className="flex flex-col gap-[18px]">
            {schema.fields.map((f) => (
              <div key={f.id}>
                <div className="mb-2 flex items-baseline justify-between">
                  <div className="text-[13px] font-medium" style={{ color: c.ink, fontFamily: typ.body }}>
                    {f.label}
                  </div>
                  {f.type === 'slider' && (
                    <div className="font-mono text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.mono }}>
                      {settings[f.id]}
                      {f.suffix || ''}
                    </div>
                  )}
                </div>
                {f.type === 'chips' && (
                  <div className="flex flex-wrap" style={{ gap: 6 }}>
                    {f.options.map((o) => {
                      const active = settings[f.id] === o.v;
                      return (
                        <button
                          key={String(o.v)}
                          type="button"
                          className="rounded-[10px] border px-3 py-[7px] font-semibold"
                          style={{
                            fontSize: 12,
                            background: active ? c.ink : 'transparent',
                            color: active ? c.bg : c.inkSoft,
                            borderColor: active ? c.ink : c.line,
                            fontFamily: typ.body,
                          }}
                          onClick={() => update(f.id, o.v)}
                        >
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                )}
                {f.type === 'slider' && (
                  <input
                    type="range"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={Number(settings[f.id])}
                    className="w-full"
                    style={{ accentColor: c.accent, height: 4 }}
                    onChange={(e) =>
                      update(f.id, f.step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value, 10))
                    }
                  />
                )}
                {f.type === 'toggle' && (
                  <button
                    type="button"
                    aria-label={f.label}
                    className="relative h-[26px] w-11 rounded-[13px]"
                    style={{ background: settings[f.id] ? c.accent : c.line }}
                    onClick={() => update(f.id, !settings[f.id])}
                  >
                    <span
                      className="absolute top-0.5 h-[22px] w-[22px] rounded-full bg-white shadow transition-[left]"
                      style={{ left: settings[f.id] ? 20 : 2 }}
                    />
                  </button>
                )}
                {id === 'spread' && f.id === 'zones' ? (
                  <div className="mt-3">
                    <SpreadRuleGuide c={c} typ={typ} settings={settings} game={game} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Btn c={c} typ={typ} variant="ghost" style={{ width: '100%' }} onClick={onRemove}>
            Remove rule
          </Btn>
        </div>
      </div>
    </div>
  );
};
